/// A generic class for communicating with REST APIs.
///
/// This is intended for use in one of two ways:
/// - By providing options in the constructor.
/// - As a subclass, overriding functions as necessary. This additionally supports creating helper functions for each
///   endpoint.
export default class RestService {
  /// Create a new service instance.
  ///
  /// The following options are available:
  /// [baseUrl]
  ///   The url to append to request paths.
  /// [defaultParams]
  ///   Parameters which will be added to every request.
  /// [defaultHeaders]
  ///   Headers which will be added to every request.
  constructor(options = {}) {
    this.baseUrl = options.baseUrl || null
    this.defaultParams = options.defaultParams || null
    this.defaultHeaders = options.defaultHeaders || null
  }

  ////////// Metadata

  /// Resolves the +request+'s +url+ parameter.
  ///
  /// This prepends the +baseUrl+ if the URL doesn't already contain a domain name.
  resolveUrlPath(url) {
    if (!url.match(/https?:\/\//) && this.baseUrl) {
      url = url.startsWith('/') ? `${this.baseUrl}${url}` : `${this.baseUrl}/${url}`
    }

    return url
  }

  ////////// Requests

  /// Performs a request.
  ///
  /// This uses the Fetch API to send a request using the given parameters:
  /// [method]
  ///   The HTTP method to use.
  /// [url]
  ///   The location of the object, either as a full URL or a URI path. When using a path, it will be combined with the
  ///   +baseUrl+.
  /// [options]
  ///   An object containing the following optional options:
  ///   [params]
  ///     An object of parameters to send with the request. This will be sent either as the URL query string or the
  ///     request body depending on the HTTP method.
  ///
  ///     This will be combined with any +defaultHeaders+, as well as any existing query parameters in the +url+.
  ///   [headers]
  ///     An object of headers to send with the request.
  ///
  ///     This will be combined with any +defaultHeaders+.
  async request(method, url, options={}) {
    url = this.resolveUrlPath(url)
    let urlQueryParams = {}

    if (url.includes('?')) {
      let queryString = url.split('?')[1]
      url = url.replace(/\?.*$/, '')
      urlQueryParams = decodeQueryString(queryString)
    }

    let headers = { ...this.defaultHeaders, ...(options.headers || {}) }
    if (!headers['Accept']) { headers['Accept'] = 'application/json' }

    let params = { ...this.defaultParams, ...urlQueryParams, ...(options.params || {}) }
    let body = null
    switch(method) {
    case 'POST':
    case 'PUT':
      if (!headers['Content-Type']) { headers['Content-Type'] = 'application/x-www-form-urlencoded' }
      switch(headers['Content-Type']) {
      case 'application/x-www-form-urlencoded':
        body = createQueryString(params)
        break
      case 'application/json':
        body = JSON.stringify(params)
        break
      }
      break
    default:
      url += `?${createQueryString(params)}`
      break
    }

    let fetchResponse = await fetch(url, {
      method: method,
      headers: headers,
      body: body,
      credentials: 'include'
    })

    return new Response(fetchResponse)
  }

  /// Performs a request.
  ///
  /// This calls +request+, passing through all arguments, and will throw an error for an unsuccessful response status.
  async REQUEST(method, url, options={}) {
    let response = await this.request(method, url, options)

    if (!response.isSuccess) {
      throw new Error(`RestService: received HTTP status ${response.status} for ${method} ${url}`)
    }

    return response
  }

  ////////// Methods

  /// Performs a DELETE request.
  ///
  /// This calls +request+ with the method set to +DELETE+.
  async delete(url, options) {
    return await this.request('DELETE', url, options)
  }

  /// Performs a DELETE request.
  ///
  /// This calls +REQUEST+ with the method set to +DELETE+.
  async DELETE(url, options) {
    return await this.REQUEST('DELETE', url, options)
  }

  /// Performs a HEAD request.
  ///
  /// This calls +request+ with the method set to +HEAD+.
  async head(url, options) {
    return await this.request('HEAD', url, options)
  }

  /// Performs a HEAD request.
  ///
  /// This calls +REQUEST+ with the method set to +HEAD+.
  async HEAD(url, options) {
    return await this.REQUEST('HEAD', url, options)
  }

  /// Performs a GET request.
  ///
  /// This calls +request+ with the method set to +GET+.
  async get(url, options) {
    return await this.request('GET', url, options)
  }

  /// Performs a GET request.
  ///
  /// This calls +REQUEST+ with the method set to +GET+.
  async GET(url, options) {
    return await this.REQUEST('GET', url, options)
  }

  /// Performs an OPTIONS request.
  ///
  /// This calls +request+ with the method set to +OPTIONS+.
  async options(url, options) {
    return await this.request('OPTIONS', url, options)
  }

  /// Performs an OPTIONS request.
  ///
  /// This calls +REQUEST+ with the method set to +OPTIONS+.
  async OPTIONS(url, options) {
    return await this.REQUEST('OPTIONS', url, options)
  }

  /// Performs a PATCH request.
  ///
  /// This calls +request+ with the method set to +PATCH+.
  async patch(url, options) {
    return await this.request('PATCH', url, options)
  }

  /// Performs a PATCH request.
  ///
  /// This calls +REQUEST+ with the method set to +PATCH+.
  async PATCH(url, options) {
    return await this.REQUEST('PATCH', url, options)
  }

  /// Performs a POST request.
  ///
  /// This calls +request+ with the method set to +POST+.
  async post(url, options) {
    return await this.request('POST', url, options)
  }

  /// Performs a POST request.
  ///
  /// This calls +REQUEST+ with the method set to +POST+.
  async POST(url, options) {
    return await this.REQUEST('POST', url, options)
  }

  /// Performs a PUT request.
  ///
  /// This calls +request+ with the method set to +PUT+.
  async put(url, options) {
    return await this.request('PUT', url, options)
  }

  /// Performs a PUT request.
  ///
  /// This calls +REQUEST+ with the method set to +PUT+.
  async PUT(url, options) {
    return await this.REQUEST('PUT', url, options)
  }
}

/// An object detailing a request response from a +RestService+.
class Response {
  /// Initialize a new response from a fetch response.
  constructor(response) {
    this.rawResponse = response
  }

  ////////// Status

  /// Retrieves the HTTP status of the response.
  get status() {
    return this.rawResponse.status
  }

  /// Indicates if the response was successful.
  get isSuccess() {
    if (typeof this._isSuccess === 'undefined') {
      this._isSuccess = (200 <= this.status && this.status < 300) || this.status === 300 || this.status === 304
    }

    return this._isSuccess
  }

  ////////// Content

  /// Retrieves the content type of the response.
  ///
  /// This may return +null+ if a content type cannot be determined.
  get contentType() {
    if (typeof this._contentType === 'undefined') {
      let headerValue = this.rawResponse.headers.get('Content-Type') || null

      if (typeof headerValue === 'string' && headerValue.length > 0) {
        if (headerValue.includes(';')) {
          headerValue = headerValue.split(';')[0]
        }
      }

      this._contentType = headerValue
    }

    return this._contentType
  }

  /// Retrieves the content of the response.
  ///
  /// The return value here differs based on the response's content type:
  /// [application/json]
  ///   The JSON-decoded value.
  /// [text/html]
  ///   A parsed document tree.
  async content() {
    if (typeof this._content !== 'undefined') { return this._content }

    switch (this.contentType) {
    case 'application/json':
      this._content = await this.rawResponse.json()
      break
    case 'text/html':
      this._content = document.createElement('html')
      this._content.innerHTML = await this.rawResponse.text()
      break
    default:
      this._content = await this.rawResponse.text()
    }

    return this._content
  }
}

/// Recursively creates a URL query string.
export function createQueryString(object, prefix = '') {
  if (object === null) {
    return ''
  } else if (Array.isArray(object)) {
    let keyName = `${prefix}[]`
    return object.map((aval) => createQueryString(aval, keyName)).join('&')
  } else if (typeof object === 'object') {
    return Object.keys(object)
      .map((key) => {
        let keyName
        if (prefix.length > 0) {
          keyName = `${prefix}[${key}]`
        } else {
          keyName = key
        }

        let oval = object[key]

        return createQueryString(oval, keyName)
      })
      .join('&')
  } else {
    let keyName = prefix
    return `${keyName}=${encodeURIComponent(object)}`
  }
}

/// Decodes a URL query string into a JS object.
export function decodeQueryString(query) {
  if (typeof query === 'string') {
    if (query.length === 0) {
      return {}
    }

    let object = {}

    let parts = query.split('&')
    parts.forEach((part) => {
      let [key, value] = part.split('=')
      key = decodeURIComponent(key)
      value = decodeURIComponent(value)

      let keyParts = key.split('[').map(k => k.replace(']', ''))
      let partIndex = 0

      let arrayValue = false
      let valueObject = object
      while (partIndex < keyParts.length - 1) {
        let objectKey = keyParts[partIndex]
        let nextKey = keyParts[partIndex + 1]

        if (Array.isArray(valueObject)) {
          let arrayIndex = 0
          while (typeof valueObject[arrayIndex] !== 'undefined' && typeof valueObject[arrayIndex][nextKey] !== 'undefined') {
            arrayIndex += 1
          }
          valueObject[arrayIndex] = valueObject[arrayIndex] || {}
          valueObject = valueObject[arrayIndex]
          arrayValue = false
        } else {
          if (nextKey === '') {
            valueObject[objectKey] = valueObject[objectKey] || []
            arrayValue = true
          } else {
            valueObject[objectKey] = valueObject[objectKey] || {}
            arrayValue = false
          }
          valueObject = valueObject[objectKey]
        }

        partIndex += 1
      }

      let objectKey = keyParts[partIndex]
      if (arrayValue) {
        valueObject[objectKey] = valueObject[objectKey] || []
        valueObject[objectKey].push(value)
      } else {
        valueObject[objectKey] = value
      }
    })

    return object
  } else {
    let object = {}
    query.forEach((value, key) => {
      object[key] = value
    })

    return object
  }
}
